/**
 * `useFollowedTeams()` -- the single hook every follow-teams UI (FollowTeamButton,
 * YourTeams, the profile "My teams" section, AccountMenu/MobileNav) reads and
 * mutates through, so the cache -- and the "at most one primary" invariant --
 * stays consistent no matter which component triggers the change.
 *
 * Mutations update the `['users', 'followedTeams', userId]` cache optimistically (so a
 * star toggle or Follow click feels instant), roll back on error with a sonner
 * toast, and invalidate on settle to reconcile with the server either way.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { userQueries } from 'queries/users';
import { followTeam, unfollowTeam } from 'services/FollowService';
import type { FollowedTeam, Team } from 'services/FollowService';
import { ApiError } from 'lib/api';
import { useAuthState } from 'lib/authState';

function errorMessage(err: unknown, fallback: string): string {
    return err instanceof ApiError && err.message ? err.message : fallback;
}

// Re-sorts primary-first-then-oldest client-side so an optimistic update
// matches the ordering the server guarantees (see the GET route in the plan).
function sortFollows(follows: FollowedTeam[]): FollowedTeam[] {
    return [...follows].sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) {
            return a.isPrimary ? -1 : 1;
        }
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
}

function withPrimary(follows: FollowedTeam[], teamId: number, isPrimary: boolean): FollowedTeam[] {
    return sortFollows(
        follows.map((f) => {
            if (f.teamId === teamId) {
                return { ...f, isPrimary };
            }
            // At most one primary: setting a new primary clears any other.
            return isPrimary && f.isPrimary ? { ...f, isPrimary: false } : f;
        })
    );
}

export function useFollowedTeams() {
    const { loggedIn, userId } = useAuthState();
    const queryClient = useQueryClient();
    const queryOptions = userQueries.followedTeams(userId);
    const queryKey = queryOptions.queryKey;
    const query = useQuery(queryOptions);

    const teams = query.data ?? [];
    const primary = teams.find((f) => f.isPrimary);

    async function optimisticUpdate(updater: (follows: FollowedTeam[]) => FollowedTeam[]) {
        await queryClient.cancelQueries({ queryKey });
        const previous = queryClient.getQueryData<FollowedTeam[]>(queryKey);
        queryClient.setQueryData<FollowedTeam[]>(queryKey, (old) => updater(old ?? []));
        return previous;
    }

    function rollback(previous: FollowedTeam[] | undefined) {
        if (previous !== undefined) {
            queryClient.setQueryData(queryKey, previous);
        }
    }

    function settle() {
        queryClient.invalidateQueries({ queryKey });
    }

    async function follow(team: Team, opts?: { isPrimary?: boolean }) {
        const isPrimary = opts?.isPrimary;
        const previous = await optimisticUpdate((follows) => {
            const existing = follows.find((f) => f.teamId === team.id);
            if (existing) {
                return isPrimary === undefined ? follows : withPrimary(follows, team.id, isPrimary);
            }
            const optimisticFollow: FollowedTeam = {
                id: -team.id,
                createdAt: new Date().toISOString(),
                userId: '',
                teamId: team.id,
                isPrimary: Boolean(isPrimary),
                team
            };
            return withPrimary([...follows, optimisticFollow], team.id, Boolean(isPrimary));
        });

        try {
            await followTeam(team.id, isPrimary);
        } catch (err) {
            rollback(previous);
            toast.error(errorMessage(err, `Couldn't follow ${team.name}.`));
        } finally {
            settle();
        }
    }

    async function unfollow(teamId: number) {
        const team = teams.find((f) => f.teamId === teamId)?.team;
        const previous = await optimisticUpdate((follows) =>
            follows.filter((f) => f.teamId !== teamId)
        );

        try {
            await unfollowTeam(teamId);
        } catch (err) {
            rollback(previous);
            toast.error(errorMessage(err, `Couldn't unfollow ${team?.name ?? 'that team'}.`));
        } finally {
            settle();
        }
    }

    async function setPrimary(teamId: number, isPrimary: boolean) {
        const team = teams.find((f) => f.teamId === teamId)?.team;
        const previous = await optimisticUpdate((follows) =>
            withPrimary(follows, teamId, isPrimary)
        );

        try {
            await followTeam(teamId, isPrimary);
        } catch (err) {
            rollback(previous);
            toast.error(
                errorMessage(
                    err,
                    isPrimary
                        ? `Couldn't make ${team?.name ?? 'that team'} your team.`
                        : `Couldn't remove ${team?.name ?? 'that team'} as your team.`
                )
            );
        } finally {
            settle();
        }
    }

    function isFollowing(teamId: number): boolean {
        return teams.some((f) => f.teamId === teamId);
    }

    return {
        teams,
        primary,
        isLoading: loggedIn && query.isLoading,
        isFollowing,
        follow,
        unfollow,
        setPrimary
    };
}
